/**
 * Minimal API client for the authenticated deSjeff endpoints.
 *
 * Security invariants:
 *  - The access token is ONLY placed in the Authorization header — it is never
 *    written to the DOM, logged, or stored outside of the oidc-client store.
 *  - A 401 from the API is surfaced as a typed error so the UI can prompt
 *    re-login instead of crashing.
 *  - The server is the authority: per-cookbook authorization is enforced by the
 *    API (and Postgres RLS). The SPA only ever sees what the caller may see — a
 *    non-member gets a 404 (no existence leak), a weak role a 403.
 */

import type {
  AdminUserListResponse,
  AdminUserSubscriptionResponse,
  CookbookAccessList,
  CookbookInvite,
  CookbookInviteRole,
  CookbookMemberSummary,
  CopyRecipeResponse,
  CreateManualShoppingItemInput,
  CreatePlannedMealInput,
  CreateRecipeInput,
  ImageUploadResponse,
  InviteCookbookMemberInput,
  ManualShoppingItem,
  MeResponse,
  PendingUser,
  PhotoImportJobCreated,
  PhotoImportJobStatus,
  PlannedMeal,
  Recipe,
  RecipeComment,
  RecipeSearchResult,
  RecipeSummary,
  ShoppingListItemState,
  ShoppingListResponse,
  UpdateManualShoppingItemInput,
  UpdatePlannedMealInput,
  UpdateRecipeInput,
  UpsertShoppingListItemStateInput,
  VideoThumbnailResponse,
} from "@tjommi/shared";
import { config } from "./config";
import {
  UNKNOWN_ENTITLEMENT_CODE,
  entitlementMessage,
  isEntitlementCode,
  type EntitlementCodeOrUnknown,
  type EntitlementDenial,
} from "./lib/entitlement";

export class UnauthorizedError extends Error {
  constructor() {
    super("Session expired or invalid — please log in again.");
    this.name = "UnauthorizedError";
  }
}

/**
 * 404 from the API. The server deliberately returns 404 (not 403) for a recipe/
 * cookbook the caller may not see, so existence is never leaked — the SPA must
 * surface this as a generic "not found", never as "exists but forbidden".
 */
export class NotFoundError extends Error {
  constructor() {
    super("Not found.");
    this.name = "NotFoundError";
  }
}

/**
 * 403 from a write — the caller is a member but lacks `recipe:write` (a read/
 * comment role). The editor entry is already role-gated, so this is a
 * defence-in-depth signal: the server is authoritative regardless of the UI.
 */
export class ForbiddenError extends Error {
  constructor() {
    super("You do not have permission to do that.");
    this.name = "ForbiddenError";
  }
}

/**
 * 503 from the AI-backed search endpoint — semantic search isn't configured
 * (no AI key) rather than a transient/server bug. The SPA maps this to a
 * friendly inline message instead of the generic error idiom.
 */
export class SearchUnavailableError extends Error {
  constructor() {
    super("Smart search is not configured on the server.");
    this.name = "SearchUnavailableError";
  }
}

/**
 * 400 from `startUrlImport` — the url is missing, too long, malformed, or not
 * http(s) (the DTO's cheap format bound; SSRF-relevant checks are the
 * server's `safeFetch` job and surface as a `failed` job status instead, not
 * this synchronous 400). The SPA maps this to a short "ugyldig lenke" inline
 * message rather than the generic error idiom.
 */
export class InvalidImportUrlError extends Error {
  constructor() {
    super("The provided URL is invalid or unsupported.");
    this.name = "InvalidImportUrlError";
  }
}

/**
 * 409 from `inviteCookbookMember` — the cookbook is already at
 * `MAX_COOKBOOK_MEMBERS` (owner + accepted members + still-pending invites).
 * The SPA maps this to a friendly Norwegian "maks antall medlemmer" message
 * rather than the generic error idiom.
 */
export class ConflictError extends Error {
  constructor() {
    super("The cookbook has reached its member cap.");
    this.name = "ConflictError";
  }
}

/**
 * 400 from the cookbook-access endpoints — a bad email/role on invite, or an
 * attempt to change/remove the cookbook's OWNER via the member endpoints
 * (never allowed; the owner row has no controls in the SPA, but the API is
 * authoritative regardless). The SPA maps this to a short inline message.
 */
export class BadRequestError extends Error {
  constructor() {
    super("The request was invalid.");
    this.name = "BadRequestError";
  }
}

/**
 * 402 from a write — the caller's ROLE is sufficient, but the cookbook's PLAN
 * doesn't include what they asked for (`/problems/entitlement-required`,
 * abonnement Fase 1). Carries the server's machine-readable `code` and, for the
 * numeric recipe limit, `limit`/`current`.
 *
 * STRICTLY DIFFERENT FROM `ForbiddenError` (403): 403 = "your role is too weak,
 * ask the owner"; 402 = "your role is fine, the plan doesn't cover this". The
 * API spends two status codes to keep them apart, so the SPA must too — never
 * collapse these two error classes or their messages.
 *
 * `message` is the SPA's own Norwegian copy (`entitlementMessage`), not the
 * server's `detail`, so even a call site that only does the generic
 * `err instanceof Error ? err.message : …` fallback shows something sensible
 * instead of "POST … failed: 402 Payment Required". `serverDetail` keeps the
 * server's prose for diagnostics only — the UI owns presentation.
 */
export class EntitlementRequiredError extends Error {
  readonly code: EntitlementCodeOrUnknown;
  readonly limit?: number;
  readonly current?: number;
  readonly serverDetail?: string;

  constructor(denial: EntitlementDenial & { serverDetail?: string }) {
    super(entitlementMessage(denial));
    this.name = "EntitlementRequiredError";
    this.code = denial.code;
    if (denial.limit !== undefined) this.limit = denial.limit;
    if (denial.current !== undefined) this.current = denial.current;
    if (denial.serverDetail !== undefined) this.serverDetail = denial.serverDetail;
  }
}

/**
 * Parse a 402 response into an `EntitlementRequiredError`.
 *
 * DEFENSIVE BY CONTRACT: a 402 we cannot fully read is still a 402. A missing
 * body, invalid JSON, a non-object body or an unknown/absent `code` degrade to
 * `UNKNOWN_ENTITLEMENT_CODE`, and a non-numeric `limit`/`current` is simply
 * dropped — never a parse exception, never the raw status text.
 * The user must always see "this needs Premium", because that is what 402 means
 * whether or not we recognise the details.
 */
async function entitlementRequiredFrom(response: Response): Promise<EntitlementRequiredError> {
  let body: unknown;
  try {
    // `json` is guarded because test doubles (and a 402 with no body at all)
    // may not provide it.
    body = typeof response.json === "function" ? await response.json() : undefined;
  } catch {
    body = undefined;
  }

  const doc: Record<string, unknown> =
    typeof body === "object" && body !== null && !Array.isArray(body)
      ? (body as Record<string, unknown>)
      : {};

  const asNumber = (value: unknown): number | undefined =>
    typeof value === "number" && Number.isFinite(value) ? value : undefined;

  return new EntitlementRequiredError({
    code: isEntitlementCode(doc.code) ? doc.code : UNKNOWN_ENTITLEMENT_CODE,
    limit: asNumber(doc.limit),
    current: asNumber(doc.current),
    serverDetail: typeof doc.detail === "string" ? doc.detail : undefined,
  });
}

/**
 * Shared fetch wrapper: attaches the bearer token, maps 401 → UnauthorizedError,
 * and any other non-2xx → a generic Error (no response body is surfaced to the
 * UI to avoid leaking server detail).
 */
async function apiGet<T>(path: string, accessToken: string): Promise<T> {
  const response = await fetch(`${config.api.baseUrl}${path}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  if (response.status === 404) {
    throw new NotFoundError();
  }

  if (!response.ok) {
    throw new Error(`GET ${path} failed: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<T>;
}

export async function fetchMe(accessToken: string): Promise<MeResponse> {
  return apiGet<MeResponse>("/me", accessToken);
}

/**
 * List the recipes in a cookbook. The caller must be a member (the API enforces
 * `cookbook:read`); a non-member receives 404, surfaced here as a generic error.
 */
export async function fetchRecipes(
  cookbookId: string,
  accessToken: string,
): Promise<RecipeSummary[]> {
  return apiGet<RecipeSummary[]>(
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes`,
    accessToken,
  );
}

/** Read a single recipe (full content). Used by the recipe view (Steg 1b-ii). */
export async function fetchRecipe(id: string, accessToken: string): Promise<Recipe> {
  return apiGet<Recipe>(`/recipes/${encodeURIComponent(id)}`, accessToken);
}

/**
 * Semantic ("smart") search across ALL of the caller's cookbooks — the API
 * scopes visibility via Postgres RLS, not a cookbookId filter (Steg 5 / F5).
 * Results come back already ranked by relevance; the caller must render them
 * in the returned order (no client-side re-sort).
 *
 * Distinct error mapping from `apiGet`: a 503 means the AI provider isn't
 * configured (not a generic server error) and is surfaced as
 * `SearchUnavailableError` so the UI can show a friendly, non-scary message.
 */
export async function searchRecipes(
  q: string,
  accessToken: string,
  limit?: number,
): Promise<RecipeSearchResult[]> {
  const params = new URLSearchParams({ q });
  if (limit != null) params.set("limit", String(limit));

  const response = await fetch(`${config.api.baseUrl}/recipes/search?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 503) throw new SearchUnavailableError();
  if (!response.ok) {
    throw new Error(`GET /recipes/search failed: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<RecipeSearchResult[]>;
}

/**
 * Shared write wrapper (POST/PATCH) with a JSON body. Same token + error mapping
 * as apiGet, plus 403 → ForbiddenError (weak role) and 404 → NotFoundError
 * (non-member / cross-cookbook). No response body is surfaced on the error path.
 *
 * 402 → `EntitlementRequiredError` lives HERE (and in the two hand-rolled
 * writes that predate this wrapper — `copyRecipe` and `inviteCookbookMember`)
 * rather than at 60 call sites. This one line covers every entitlement-gated
 * endpoint that goes through the wrapper: recipe creation plus all six paid
 * import calls (photo/video/pdf × upload-url + start). The 402 body IS read
 * here — unlike other errors — because it is a documented, caller-safe
 * problem+json contract, not server internals.
 */
async function apiSend<T>(
  method: "POST" | "PATCH",
  path: string,
  body: unknown,
  accessToken: string,
): Promise<T> {
  const response = await fetch(`${config.api.baseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 402) throw await entitlementRequiredFrom(response);
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(`${method} ${path} failed: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<T>;
}

/**
 * Create a recipe in a cookbook (needs `recipe:write` — owner/write member).
 * The editor only POSTs after the active cookbook's role is checked, but the
 * API + RLS remain authoritative (a weak role → ForbiddenError, non-member →
 * NotFoundError).
 */
export async function createRecipe(
  cookbookId: string,
  input: CreateRecipeInput,
  accessToken: string,
): Promise<Recipe> {
  return apiSend<Recipe>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes`,
    input,
    accessToken,
  );
}

/** Partially update a recipe (needs `recipe:write`). Used by autosave. */
export async function updateRecipe(
  id: string,
  input: UpdateRecipeInput,
  accessToken: string,
): Promise<Recipe> {
  return apiSend<Recipe>("PATCH", `/recipes/${encodeURIComponent(id)}`, input, accessToken);
}

/**
 * Request a short-lived presigned upload URL for a recipe image (needs
 * `recipe:write`; server-side allow-list on `contentType` — Steg 3a). The
 * caller then PUTs the file bytes directly to `uploadUrl` (a plain fetch, not
 * this client — see `RecipeEditorPage`), and PATCHes the recipe with `key`.
 */
export async function requestImageUpload(
  recipeId: string,
  contentType: string,
  accessToken: string,
): Promise<ImageUploadResponse> {
  return apiSend<ImageUploadResponse>(
    "POST",
    `/recipes/${encodeURIComponent(recipeId)}/images`,
    { contentType },
    accessToken,
  );
}

/**
 * Delete a recipe (needs `recipe:write` — owner/write member). Returns nothing
 * (204 No Content, so no body is parsed). Same error mapping as the write
 * wrapper; the API + RLS remain authoritative (weak role → ForbiddenError,
 * non-member / already-gone → NotFoundError).
 */
export async function deleteRecipe(id: string, accessToken: string): Promise<void> {
  const response = await fetch(`${config.api.baseUrl}/recipes/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(`DELETE /recipes/${id} failed: ${response.status} ${response.statusText}`);
  }
}

/**
 * Mark a `needs_review` recipe (pdf-import, Slice 3b) as reviewed — the
 * one-click "approve from the feed" affordance (`RecipeCard`'s × on the "Til
 * gjennomgang" badge). Needs `recipe:write` (owner/write member; a
 * read/comment member gets 403, a non-member 404 — same mapping as the rest
 * of this file). Idempotent server-side (re-approving an already-reviewed
 * recipe is a no-op 200, not an error). Deliberately does NOT bump
 * `updatedAt`, so the feed's most-recently-updated ordering is unaffected by
 * approving.
 */
export async function markRecipeReviewed(recipeId: string, accessToken: string): Promise<Recipe> {
  return apiSend<Recipe>(
    "POST",
    `/recipes/${encodeURIComponent(recipeId)}/reviewed`,
    undefined,
    accessToken,
  );
}

/**
 * Copy a recipe into the CALLER'S OWN cookbook (pilot "Kopier til min
 * kokebok"). No request body — the target cookbook is derived server-side
 * from the caller's own cookbook. The read view only offers this action when
 * the viewer's role on the recipe's cookbook isn't "owner" (a UI convenience;
 * the API is authoritative regardless).
 *
 * Distinct error mapping: 409 means the recipe is already in the caller's own
 * cookbook, or the caller has no owned cookbook to copy into — surfaced as
 * `ConflictError` (reused from the invite-cap idiom above) so the UI can show
 * a friendly inline message instead of the generic error path.
 */
export async function copyRecipe(
  recipeId: string,
  accessToken: string,
): Promise<CopyRecipeResponse> {
  const response = await fetch(
    `${config.api.baseUrl}/recipes/${encodeURIComponent(recipeId)}/copy`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  // 402: copying would push the CALLER'S OWN cookbook past the free recipe
  // limit (`code: "recipe_limit"`). Same mapping as `apiSend`'s — this function
  // predates that wrapper and keeps its own status ladder.
  if (response.status === 402) throw await entitlementRequiredFrom(response);
  if (response.status === 404) throw new NotFoundError();
  if (response.status === 409) throw new ConflictError();
  if (!response.ok) {
    throw new Error(
      `POST /recipes/${recipeId}/copy failed: ${response.status} ${response.statusText}`,
    );
  }

  return response.json() as Promise<CopyRecipeResponse>;
}

/**
 * List users awaiting approval (closed-pilot admin queue). Admin-gated
 * server-side: the API re-checks the caller's admin allowlist from the
 * verified token regardless of the SPA's `me.isAdmin` hint (a non-admin gets
 * 403, surfaced here as `ForbiddenError`).
 */
export async function fetchPendingUsers(accessToken: string): Promise<PendingUser[]> {
  return apiGet<PendingUser[]>("/admin/users/pending", accessToken);
}

/**
 * Approve a pending user (admin-only). Returns nothing (200/204, no body of
 * interest is parsed — same shape as `deleteRecipe`). The subject is
 * URL-encoded to keep it out of the path structure.
 */
export async function approveUser(subject: string, accessToken: string): Promise<void> {
  const response = await fetch(
    `${config.api.baseUrl}/admin/users/${encodeURIComponent(subject)}/approve`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `POST /admin/users/${subject}/approve failed: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Request a short-lived presigned upload URL for a cookbook-page photo to
 * import (needs `recipe:write`; Steg 5 photo/OCR import). Mints a TRANSIENT
 * object-storage key (a namespace disjoint from persisted recipe images) —
 * the caller PUTs the file bytes directly to `uploadUrl` (a plain fetch, not
 * this client), then calls `startPhotoImport` with the returned `key`.
 */
export async function requestPhotoImportUpload(
  cookbookId: string,
  contentType: string,
  accessToken: string,
): Promise<ImageUploadResponse> {
  return apiSend<ImageUploadResponse>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes/import/photo/upload-url`,
    { contentType },
    accessToken,
  );
}

/**
 * Start an ASYNC photo-import job from an already-uploaded cookbook-page
 * photo (needs `recipe:write`). The Gemini vision call behind this has been
 * measured at 21s-198s — well past a mobile browser's ~60s fetch timeout — so
 * this call only creates a job and returns immediately (202 Accepted); the
 * caller must poll `getImportJob` with the returned `jobId` for the eventual
 * draft. Nothing is persisted as a recipe by either call — see
 * `RecipeImportDraft`'s REVIEW-BEFORE-SAVE docs.
 */
export async function startPhotoImport(
  cookbookId: string,
  keys: string[],
  accessToken: string,
): Promise<PhotoImportJobCreated> {
  return apiSend<PhotoImportJobCreated>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes/import/photo`,
    { keys },
    accessToken,
  );
}

/**
 * Start an ASYNC url-import job from a source recipe page's url (needs
 * `recipe:write`). The page is fetched server-side through an SSRF-safe
 * fetcher; extraction prefers the page's schema.org/Recipe JSON-LD, falling
 * back to an LLM reading the readable text. This call only validates the
 * request, creates a job, and returns its `jobId` immediately (202
 * Accepted) — poll `getImportJob` with the returned id for the eventual
 * draft, exactly like `startPhotoImport`. A malformed/blank/over-long url
 * 400s synchronously, mapped here to `InvalidImportUrlError`.
 */
export async function startUrlImport(
  cookbookId: string,
  url: string,
  accessToken: string,
): Promise<PhotoImportJobCreated> {
  const response = await fetch(
    `${config.api.baseUrl}/cookbooks/${encodeURIComponent(cookbookId)}/recipes/import/url`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ url }),
    },
  );

  // NO 402 MAPPING HERE, DELIBERATELY: website import is free on every plan
  // (§1 — it is cheap enough to be unmetered), so the API has no entitlement
  // gate on this endpoint and never answers 402 for it. Adding a mapping would
  // imply a gate that doesn't exist; this is the one import path a Free user
  // must always be able to finish.
  if (response.status === 400) throw new InvalidImportUrlError();
  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `POST /cookbooks/${cookbookId}/recipes/import/url failed: ${response.status} ${response.statusText}`,
    );
  }

  return response.json() as Promise<PhotoImportJobCreated>;
}

/**
 * Poll an import job's status — SOURCE-AGNOSTIC (photo or url; the API route
 * is the same for both, see `RecipeImportController`). Needs `recipe:write`;
 * a job is strictly private to its creator — polling someone else's, or an
 * unknown, jobId 404s identically. `draft` is present only once
 * `status === "done"`; `error` (a short Norwegian reason) only once
 * `status === "failed"`.
 *
 * BREAKING CHANGE (formerly `getPhotoImportJob`, formerly a photo-only path):
 * the poll route used to be `.../import/photo/jobs/{jobId}`; it is now the
 * source-agnostic `.../import/jobs/{jobId}` below — the old path has been
 * REMOVED server-side.
 */
export async function getImportJob(
  cookbookId: string,
  jobId: string,
  accessToken: string,
): Promise<PhotoImportJobStatus> {
  return apiGet<PhotoImportJobStatus>(
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes/import/jobs/${encodeURIComponent(jobId)}`,
    accessToken,
  );
}

/**
 * Request a short-lived presigned upload URL for a video to import (needs
 * `recipe:write`; video-import, Slice 4). Mints a TRANSIENT object-storage key
 * (namespace `imports/{cookbookId}/videos/…`) — the caller PUTs the file
 * bytes directly to `uploadUrl` (a plain fetch, not this client), then calls
 * `startVideoImport` with the returned `key` as `uploadedKey`.
 */
export async function requestVideoImportUpload(
  cookbookId: string,
  contentType: string,
  accessToken: string,
): Promise<ImageUploadResponse> {
  return apiSend<ImageUploadResponse>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes/import/video/upload-url`,
    { contentType },
    accessToken,
  );
}

/** Body of `startVideoImport` — EXACTLY ONE of `url`/`uploadedKey` (never
 * both, never neither — the caller picks a mode and only sets the matching
 * field); `ingredientText` is always optional. */
export interface StartVideoImportInput {
  url?: string;
  uploadedKey?: string;
  ingredientText?: string;
}

/**
 * Start an ASYNC video-import job (needs `recipe:write`; video-import, Slice
 * 4) — from a public video url (YouTube or a direct video link) OR an
 * already-uploaded video (`uploadedKey`, from `requestVideoImportUpload`),
 * optionally with a pasted-in `ingredientText`. This call only validates the
 * request, creates a job, and returns its `jobId` immediately (202 Accepted);
 * poll `getImportJob` for the eventual draft or failure, exactly like
 * `startPhotoImport`/`startUrlImport`. Nothing is persisted as a recipe by
 * either call — see `RecipeImportDraft`'s REVIEW-BEFORE-SAVE docs.
 */
export async function startVideoImport(
  cookbookId: string,
  input: StartVideoImportInput,
  accessToken: string,
): Promise<PhotoImportJobCreated> {
  return apiSend<PhotoImportJobCreated>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes/import/video`,
    input,
    accessToken,
  );
}

/**
 * Ask the API to fetch + store a preview thumbnail for a pasted video link
 * (needs `recipe:write`; video-link, Slice C). The API fetches the still
 * server-side and stores it as our own object — we never hotlink a third
 * party's image into the client (privacy). NEVER an error response for a
 * video/page that yields no usable thumbnail: both fields come back `null`
 * and the link itself still saves fine without a preview (see
 * `VideoThumbnailResponse`'s docs in @tjommi/shared).
 */
export async function requestVideoThumbnail(
  cookbookId: string,
  videoUrl: string,
  accessToken: string,
): Promise<VideoThumbnailResponse> {
  return apiSend<VideoThumbnailResponse>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes/video-thumbnail`,
    { videoUrl },
    accessToken,
  );
}

/**
 * Request a short-lived presigned upload URL for a cookbook-scan PDF to
 * import (needs `recipe:write`; pdf-import, Slice 3b). Mints a TRANSIENT
 * object-storage key (namespace `imports/{cookbookId}/pdfs/…`) — the caller
 * PUTs the file bytes directly to `uploadUrl` (a plain fetch, not this
 * client), then calls `startPdfImport` with the returned `key` as
 * `uploadedKey`. No request body: there is exactly one accepted content type
 * (`application/pdf`).
 */
export async function requestPdfImportUpload(
  cookbookId: string,
  accessToken: string,
): Promise<ImageUploadResponse> {
  return apiSend<ImageUploadResponse>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes/import/pdf/upload-url`,
    undefined,
    accessToken,
  );
}

/** Body of `startPdfImport` — the only field is the already-uploaded key. */
export interface StartPdfImportInput {
  uploadedKey: string;
}

/**
 * Start an ASYNC pdf-import job (needs `recipe:write`; pdf-import, Slice 3b)
 * from an already-uploaded PDF (`uploadedKey`, from
 * `requestPdfImportUpload`). UNLIKE `startPhotoImport`/`startUrlImport`/
 * `startVideoImport`, this does not produce an unsaved draft: the background
 * worker extracts EVERY recipe found in the PDF and PERSISTS them directly
 * into the cookbook, each flagged `reviewState: 'needs_review'`. This call
 * only validates the request, creates a job, and returns its `jobId`
 * immediately (202 Accepted); poll `getImportJob` for the eventual
 * `importedRecipeIds`/`importedCount` (or failure).
 */
export async function startPdfImport(
  cookbookId: string,
  input: StartPdfImportInput,
  accessToken: string,
): Promise<PhotoImportJobCreated> {
  return apiSend<PhotoImportJobCreated>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/recipes/import/pdf`,
    input,
    accessToken,
  );
}

/**
 * List (a page of) the full admin user directory (Steg D) — every user,
 * approved or not, server-side searched via `q` and server-side paginated via
 * `limit`/`offset`. `q` is omitted entirely when blank (rather than sent as an
 * empty string) so the API sees a plain unfiltered listing.
 */
export async function fetchAllUsers(
  accessToken: string,
  params: { q?: string; limit?: number; offset?: number } = {},
): Promise<AdminUserListResponse> {
  const search = new URLSearchParams();
  if (params.q != null && params.q !== "") search.set("q", params.q);
  search.set("limit", String(params.limit ?? 25));
  search.set("offset", String(params.offset ?? 0));

  return apiGet<AdminUserListResponse>(`/admin/users?${search.toString()}`, accessToken);
}

/**
 * Shared POST wrapper for the admin licence endpoints (abonnement Fase 1 /
 * Slice 5). Unlike `apiSend` this also maps 400 → `BadRequestError` and
 * 409 → `ConflictError`, because both are states the admin UI must explain in
 * its own words:
 *   - 400: the server rejected the reason/expiry (its validation is the
 *     authoritative one — the client's is only a courtesy),
 *   - 409: the user already holds an active licence, which is information, not
 *     a mistake.
 * `body` is omitted entirely for revoke (that endpoint takes no body), so no
 * `Content-Type` is sent for it either.
 */
async function postEntitlement(
  path: string,
  body: unknown | undefined,
  accessToken: string,
): Promise<AdminUserSubscriptionResponse> {
  const response = await fetch(`${config.api.baseUrl}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (response.status === 409) throw new ConflictError();
  if (response.status === 400) throw new BadRequestError();
  if (!response.ok) {
    throw new Error(`POST ${path} failed: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<AdminUserSubscriptionResponse>;
}

/**
 * Give a user a FREE Premium licence (admin-only; `entitlement_grants`).
 * `reason` is required (1–200 chars, §4's audit trail); `expiresAt` is an ISO
 * timestamp in the future, or null/omitted for a permanent licence.
 *
 * Returns the target's subscription state AFTER the change so the caller can
 * patch that one directory row in place. There is deliberately NO client
 * function for creating or editing a PAYING subscription: that table is
 * provider-owned and the API exposes no write path to it (§4).
 */
export async function grantUserEntitlement(
  subject: string,
  input: { reason: string; expiresAt?: string | null },
  accessToken: string,
): Promise<AdminUserSubscriptionResponse> {
  return postEntitlement(
    `/admin/users/${encodeURIComponent(subject)}/entitlement-grants`,
    input,
    accessToken,
  );
}

/**
 * Withdraw a user's free Premium licence (admin-only). Idempotent — a user
 * with no active licence yields `revokedCount: 0` and still succeeds. Takes no
 * arguments on purpose: revocation is immediate, so there is no "revoke from
 * date" (schedule the end of a licence with `expiresAt` on the grant instead).
 * Never touches a paying subscription.
 */
export async function revokeUserEntitlement(
  subject: string,
  accessToken: string,
): Promise<AdminUserSubscriptionResponse> {
  return postEntitlement(
    `/admin/users/${encodeURIComponent(subject)}/entitlement-grants/revoke`,
    undefined,
    accessToken,
  );
}

/**
 * Cookbook sharing & invites (P2-F2) — every endpoint below is OWNER-only
 * server-side (`cookbook:manage`); the SPA's `CookbookManagePage` also gates
 * entry on the caller's role as a UI hint only, same idiom as `recipe:write`
 * elsewhere in this file.
 */

/**
 * List a cookbook's members + still-pending invites (the owner's manage
 * view). A non-owner member gets 403 (surfaced as a generic Error here — the
 * page itself redirects a non-owner away before ever calling this), a
 * non-member 404.
 */
export async function fetchCookbookAccess(
  cookbookId: string,
  accessToken: string,
): Promise<CookbookAccessList> {
  return apiGet<CookbookAccessList>(
    `/cookbooks/${encodeURIComponent(cookbookId)}/access`,
    accessToken,
  );
}

/**
 * Invite a member by email with `role: "write" | "read"`. Distinct error
 * mapping from the shared `apiSend` wrapper: 409 means the cookbook is
 * already at `MAX_COOKBOOK_MEMBERS` (`ConflictError`), 400 a bad email/role
 * (`BadRequestError`) — both surfaced as short inline Norwegian messages by
 * the manage page rather than the generic error idiom.
 */
export async function inviteCookbookMember(
  cookbookId: string,
  input: InviteCookbookMemberInput,
  accessToken: string,
): Promise<CookbookInvite> {
  const response = await fetch(
    `${config.api.baseUrl}/cookbooks/${encodeURIComponent(cookbookId)}/invites`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  // 402: sharing isn't in the cookbook's plan at all (`code: "sharing"`).
  // DIFFERENT from the 409 below (Premium's member cap reached) and from the
  // 403 above (not the owner) — three distinct answers, three distinct
  // messages.
  if (response.status === 402) throw await entitlementRequiredFrom(response);
  if (response.status === 404) throw new NotFoundError();
  if (response.status === 409) throw new ConflictError();
  if (response.status === 400) throw new BadRequestError();
  if (!response.ok) {
    throw new Error(
      `POST /cookbooks/${cookbookId}/invites failed: ${response.status} ${response.statusText}`,
    );
  }

  return response.json() as Promise<CookbookInvite>;
}

/**
 * Change a member's role to `write` or `read`. 400 if `subject` is the
 * cookbook's owner (their role can never be changed here) — surfaced as
 * `BadRequestError`.
 */
export async function updateCookbookMemberRole(
  cookbookId: string,
  subject: string,
  role: CookbookInviteRole,
  accessToken: string,
): Promise<CookbookMemberSummary> {
  const response = await fetch(
    `${config.api.baseUrl}/cookbooks/${encodeURIComponent(cookbookId)}/members/${encodeURIComponent(subject)}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ role }),
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (response.status === 400) throw new BadRequestError();
  if (!response.ok) {
    throw new Error(
      `PATCH /cookbooks/${cookbookId}/members/${subject} failed: ${response.status} ${response.statusText}`,
    );
  }

  return response.json() as Promise<CookbookMemberSummary>;
}

/**
 * Revoke a member (immediate, no grace period). 400 if `subject` is the
 * cookbook's owner (they can never be removed here) — surfaced as
 * `BadRequestError`. Returns nothing (204 No Content, no body parsed).
 */
export async function revokeCookbookMember(
  cookbookId: string,
  subject: string,
  accessToken: string,
): Promise<void> {
  const response = await fetch(
    `${config.api.baseUrl}/cookbooks/${encodeURIComponent(cookbookId)}/members/${encodeURIComponent(subject)}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (response.status === 400) throw new BadRequestError();
  if (!response.ok) {
    throw new Error(
      `DELETE /cookbooks/${cookbookId}/members/${subject} failed: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Cancel a still-pending invite. Returns nothing (204 No Content, no body
 * parsed).
 */
export async function cancelCookbookInvite(
  cookbookId: string,
  inviteId: string,
  accessToken: string,
): Promise<void> {
  const response = await fetch(
    `${config.api.baseUrl}/cookbooks/${encodeURIComponent(cookbookId)}/invites/${encodeURIComponent(inviteId)}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `DELETE /cookbooks/${cookbookId}/invites/${inviteId} failed: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Recipe comments + likes (P2-F6). Commenting/liking is NOT role-gated — every
 * cookbook member may comment on and like a recipe's comments (see
 * `RecipeComment`'s docs in @tjommi/shared); deletes are the one asymmetric
 * rule (own comment, or the cookbook owner moderating) enforced server-side.
 */

/** List a recipe's comments, oldest-first (the API's own ordering — do not re-sort). */
export async function fetchComments(
  recipeId: string,
  accessToken: string,
): Promise<RecipeComment[]> {
  return apiGet<RecipeComment[]>(`/recipes/${encodeURIComponent(recipeId)}/comments`, accessToken);
}

/** Post a new comment. `body` should already be trimmed by the caller. */
export async function createComment(
  recipeId: string,
  body: string,
  accessToken: string,
): Promise<RecipeComment> {
  return apiSend<RecipeComment>(
    "POST",
    `/recipes/${encodeURIComponent(recipeId)}/comments`,
    { body },
    accessToken,
  );
}

/**
 * Delete a comment (own comment, or cookbook-owner moderation). Returns
 * nothing (204 No Content, no body parsed) — same idiom as `deleteRecipe`. A
 * 404 means the comment is already gone (or no longer visible/permitted) and
 * the caller should treat that as success.
 */
export async function deleteComment(commentId: string, accessToken: string): Promise<void> {
  const response = await fetch(`${config.api.baseUrl}/comments/${encodeURIComponent(commentId)}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `DELETE /comments/${commentId} failed: ${response.status} ${response.statusText}`,
    );
  }
}

/** Like a comment (idempotent — liking twice is a no-op server-side). 204, no body. */
export async function likeComment(commentId: string, accessToken: string): Promise<void> {
  const response = await fetch(
    `${config.api.baseUrl}/comments/${encodeURIComponent(commentId)}/likes`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `POST /comments/${commentId}/likes failed: ${response.status} ${response.statusText}`,
    );
  }
}

/** Un-like a comment (idempotent — unliking twice is a no-op server-side). 204, no body. */
export async function unlikeComment(commentId: string, accessToken: string): Promise<void> {
  const response = await fetch(
    `${config.api.baseUrl}/comments/${encodeURIComponent(commentId)}/likes`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `DELETE /comments/${commentId}/likes failed: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Weekly meal planner (Ukeplanlegger, Slice 3). Ownership is per-cookbook
 * (shared with every member): any member may GET, only owner/write may
 * POST/PATCH/DELETE (403 otherwise, mirrored client-side via
 * `roleCanEditRecipes`). `weekStart` MUST be that week's Monday
 * (`YYYY-MM-DD`, see `lib/week.ts`'s `mondayString`/`getWeek`) — the API
 * 400s on anything else, surfaced here as the generic error path (a
 * programmer error, not a user-facing case).
 */

/** The week's planned meals (any day, any position) — already denormalized with recipe title/servings/hero image, so no per-meal fetch is needed. */
export async function fetchMealPlanWeek(
  cookbookId: string,
  weekStart: string,
  accessToken: string,
): Promise<PlannedMeal[]> {
  const params = new URLSearchParams({ weekStart });
  return apiGet<PlannedMeal[]>(
    `/cookbooks/${encodeURIComponent(cookbookId)}/meal-plan?${params.toString()}`,
    accessToken,
  );
}

/** Add a recipe to the plan on a given date (needs `recipe:write`). */
export async function addPlannedMeal(
  cookbookId: string,
  input: CreatePlannedMealInput,
  accessToken: string,
): Promise<PlannedMeal> {
  return apiSend<PlannedMeal>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/meal-plan`,
    input,
    accessToken,
  );
}

/** Change a planned meal's servings (needs `recipe:write`) — used by the calendar-entry stepper. */
export async function updatePlannedMealServings(
  entryId: string,
  servings: number,
  accessToken: string,
): Promise<PlannedMeal> {
  const body: UpdatePlannedMealInput = { servings };
  return apiSend<PlannedMeal>(
    "PATCH",
    `/meal-plan/${encodeURIComponent(entryId)}`,
    body,
    accessToken,
  );
}

/** Remove a planned meal (needs `recipe:write`). Returns nothing (204 No Content). */
export async function deletePlannedMeal(entryId: string, accessToken: string): Promise<void> {
  const response = await fetch(`${config.api.baseUrl}/meal-plan/${encodeURIComponent(entryId)}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `DELETE /meal-plan/${entryId} failed: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Shopping list (Ukeplanlegger, Slice 5). Same ownership model as the meal
 * planner: any member GETs, only owner/write mutate (403 otherwise, mirrored
 * client-side via `roleCanEditRecipes`). `weekStart` MUST be that week's
 * Monday — reuse `lib/week.ts`'s `mondayString`/`getWeek`, never recompute.
 */

/**
 * The full shopping list for a week: the merged, already-sorted
 * recipe-derived lines (`fromRecipes`, including `removed: true` ones — the
 * caller hides those) plus the independent manual ("Annet") items. Do NOT
 * re-sort, re-group or re-merge either array client-side.
 */
export async function fetchShoppingList(
  cookbookId: string,
  weekStart: string,
  accessToken: string,
): Promise<ShoppingListResponse> {
  const params = new URLSearchParams({ weekStart });
  return apiGet<ShoppingListResponse>(
    `/cookbooks/${encodeURIComponent(cookbookId)}/shopping-list?${params.toString()}`,
    accessToken,
  );
}

/** Add a manual ("Annet") item to the week's list (needs `recipe:write`). */
export async function addManualShoppingItem(
  cookbookId: string,
  input: CreateManualShoppingItemInput,
  accessToken: string,
): Promise<ManualShoppingItem> {
  return apiSend<ManualShoppingItem>(
    "POST",
    `/cookbooks/${encodeURIComponent(cookbookId)}/shopping-list/manual-items`,
    input,
    accessToken,
  );
}

/** Update a manual item's `checked`/`text` (needs `recipe:write`) — partial. */
export async function updateManualShoppingItem(
  itemId: string,
  input: UpdateManualShoppingItemInput,
  accessToken: string,
): Promise<ManualShoppingItem> {
  return apiSend<ManualShoppingItem>(
    "PATCH",
    `/shopping-list/manual-items/${encodeURIComponent(itemId)}`,
    input,
    accessToken,
  );
}

/** Remove a manual item (needs `recipe:write`). Returns nothing (204 No Content). */
export async function deleteManualShoppingItem(itemId: string, accessToken: string): Promise<void> {
  const response = await fetch(
    `${config.api.baseUrl}/shopping-list/manual-items/${encodeURIComponent(itemId)}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `DELETE /shopping-list/manual-items/${itemId} failed: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Idempotent upsert of one recipe-derived line's `checked`/`removed` state
 * (needs `recipe:write`). Omitted fields are preserved server-side (COALESCE)
 * — send only what changed, same idiom as `UpdatePlannedMealInput`.
 */
export async function setShoppingItemState(
  cookbookId: string,
  input: UpsertShoppingListItemStateInput,
  accessToken: string,
): Promise<ShoppingListItemState> {
  const response = await fetch(
    `${config.api.baseUrl}/cookbooks/${encodeURIComponent(cookbookId)}/shopping-list/item-state`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );

  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new ForbiddenError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok) {
    throw new Error(
      `PATCH /cookbooks/${cookbookId}/shopping-list/item-state failed: ${response.status} ${response.statusText}`,
    );
  }

  return response.json() as Promise<ShoppingListItemState>;
}
